import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Book as BookIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getClassLevelsManagement, getClassLevelStats } from '../api/api';

interface ClassLevel {
  key: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  student_count: number;
  course_count: number;
}

interface ClassLevelStats {
  class_level: {
    key: string;
    display_name: string;
  };
  stats: {
    student_count: number;
    course_count: number;
    students: Array<{
      id: number;
      name: string;
      profile_picture?: string;
    }>;
    courses: Array<{
      id: number;
      title: string;
      course_code: string;
    }>;
  };
}

const ClassLevelManagement: React.FC = () => {
  const { user } = useAuth();
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClassLevel, setSelectedClassLevel] = useState<ClassLevel | null>(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [classLevelStats, setClassLevelStats] = useState<ClassLevelStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superuser') {
      fetchClassLevels();
    }
  }, [user]);

  const fetchClassLevels = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getClassLevelsManagement();
      setClassLevels(response.data as ClassLevel[]);
    } catch (err: any) {
      setError('Failed to load class levels');
      console.error('Error fetching class levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewStats = async (classLevel: ClassLevel) => {
    setSelectedClassLevel(classLevel);
    setStatsDialogOpen(true);
    setStatsLoading(true);
    setClassLevelStats(null);
    
    try {
      const response = await getClassLevelStats(classLevel.key);
      setClassLevelStats(response.data as ClassLevelStats | null);
    } catch (err: any) {
      setError('Failed to load class level statistics');
      console.error('Error fetching class level stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBulkAssignStudents = async (classLevel: ClassLevel) => {
    // This would open a dialog to select students and assign them to this class level
    // For now, we'll just show an alert
    alert(`Bulk assign students to ${classLevel.display_name} - Feature coming soon!`);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  if (user?.role !== 'admin' && user?.role !== 'superuser') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You are not authorized to view this page.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
        Class Level Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 3 }}>
        {classLevels.map((classLevel) => (
          <Box key={classLevel.key}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    {classLevel.display_name}
                  </Typography>
                </Box>

                <Chip
                  label={getStatusText(classLevel.is_active)}
                  color={getStatusColor(classLevel.is_active)}
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {classLevel.student_count} Students
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BookIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {classLevel.course_count} Courses
                    </Typography>
                  </Box>
                </Box>

                {classLevel.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {classLevel.description}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={() => handleViewStats(classLevel)}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Bulk Assign Students">
                  <IconButton
                    size="small"
                    onClick={() => handleBulkAssignStudents(classLevel)}
                    color="secondary"
                  >
                    <GroupIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Class Level Statistics Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
            {selectedClassLevel?.display_name} - Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : classLevelStats ? (
            <Box>
              {/* Statistics Summary */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Badge badgeContent={classLevelStats.stats.student_count} color="primary">
                      <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Badge>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Students
                    </Typography>
                  </Paper>
                </Box>
                <Box>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Badge badgeContent={classLevelStats.stats.course_count} color="secondary">
                      <BookIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                    </Badge>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Courses
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Students List */}
              <Typography variant="h6" gutterBottom>
                Students ({classLevelStats.stats.students.length})
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                {classLevelStats.stats.students.map((student) => (
                  <ListItem key={student.id} dense>
                    <ListItemAvatar>
                      <Avatar src={student.profile_picture}>
                        {student.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={student.name} />
                  </ListItem>
                ))}
                {classLevelStats.stats.students.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No students enrolled" />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              {/* Courses List */}
              <Typography variant="h6" gutterBottom>
                Courses ({classLevelStats.stats.courses.length})
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {classLevelStats.stats.courses.map((course) => (
                  <ListItem key={course.id} dense>
                    <ListItemAvatar>
                      <Avatar>
                        <BookIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={course.title}
                      secondary={course.course_code}
                    />
                  </ListItem>
                ))}
                {classLevelStats.stats.courses.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No courses assigned" />
                  </ListItem>
                )}
              </List>
            </Box>
          ) : (
            <Alert severity="error">Failed to load class level statistics</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassLevelManagement; 